// Delivery Management + Proof of Delivery helpers (Firestore + Firebase Storage).
import { db, storage } from "./firebase";
import {
  collection, doc, setDoc, getDoc, getDocs, onSnapshot, query, where,
  updateDoc, addDoc, serverTimestamp
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { ACS_CLIENT, ACS_JOB, ACS_STOPS } from "../data/acsJob";

export const STOP_STATUS_META = {
  pending: { label: "Pending", cls: "bg-slate-100 text-slate-700 border-slate-200" },
  in_transit: { label: "In Transit", cls: "bg-blue-100 text-blue-700 border-blue-200" },
  delivered: { label: "Delivered", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  attempted: { label: "Attempted", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  failed: { label: "Failed", cls: "bg-red-100 text-red-700 border-red-200" }
};

export function genToken(len = 24) {
  const chars = "abcdefghijklmnopqrstuvwxyz0123456789";
  let s = "";
  const arr = new Uint32Array(len);
  (window.crypto || window.msCrypto).getRandomValues(arr);
  for (let i = 0; i < len; i++) s += chars[arr[i] % chars.length];
  return s;
}

export function stopDocId(jobId, stopNumber) {
  return `${jobId}-${String(stopNumber).padStart(2, "0")}`;
}

// Seed the ACS job idempotently (fixed doc ids).
export async function seedAcsJob() {
  const jobId = ACS_JOB.job_reference;
  const jobRef = doc(db, "delivery_jobs", jobId);
  const snap = await getDoc(jobRef);
  if (snap.exists()) return jobId;

  await setDoc(doc(db, "clients", ACS_CLIENT.client_id), { ...ACS_CLIENT, createdAt: serverTimestamp() }, { merge: true });
  await setDoc(jobRef, {
    ...ACS_JOB,
    client_id: ACS_CLIENT.client_id,
    client_name: ACS_CLIENT.client_name,
    total_stops: ACS_STOPS.length,
    completed_stops: 0,
    driver_id: null,
    driver_email: "",
    track_token: genToken(),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
  for (const s of ACS_STOPS) {
    const id = stopDocId(jobId, s.stop_number);
    await setDoc(doc(db, "delivery_stops", id), {
      stop_number: s.stop_number,
      recipient_name: s.recipient_name,
      company_name: s.company_name || "",
      address_line_1: s.address_line_1,
      address_line_2: s.address_line_2 || "",
      city: s.city, state: s.state, zip_code: s.zip_code,
      phone: s.phone || "",
      delivery_instructions: s.delivery_instructions || "",
      package_reference: s.package_reference,
      job_id: jobId,
      driver_email: "",
      status: "pending",
      arrival_time: null,
      delivered_time: null,
      recipient_received_by: "",
      driver_notes: "",
      latitude: null, longitude: null,
      pod_id: null, photo_url: null, signature_url: null,
      createdAt: serverTimestamp()
    });
  }
  await addAudit(jobId, null, "Job created (ACS Sep 30 route seeded)", "system");
  await writePublicTracking(jobId);
  return jobId;
}

export async function createJob({ job_name, client_name, job_date, pickup_address, contract_amount, notes, driver_email, notify_emails }) {
  const jobRef = await addDoc(collection(db, "delivery_jobs"), {
    job_reference: `JOB-${Date.now()}`,
    job_name, client_name: client_name || "",
    job_date: job_date || "", pickup_address: pickup_address || "",
    contract_amount: Number(contract_amount) || 0,
    notes: notes || "", driver_email: (driver_email || "").toLowerCase(), driver_id: null,
    notify_emails: Array.isArray(notify_emails) ? notify_emails : [],
    job_status: "Scheduled", total_stops: 0, completed_stops: 0,
    track_token: genToken(), createdAt: serverTimestamp(), updatedAt: serverTimestamp()
  });
  await addAudit(jobRef.id, null, "Job created", "admin");
  return jobRef.id;
}

export async function addStop(jobId, stop, currentCount, driverEmail) {
  const stopNumber = (currentCount || 0) + 1;
  const id = stopDocId(jobId, stopNumber);
  await setDoc(doc(db, "delivery_stops", id), {
    ...stop, job_id: jobId, stop_number: stopNumber, status: "pending",
    driver_email: (driverEmail || "").toLowerCase(),
    arrival_time: null, delivered_time: null, recipient_received_by: "",
    driver_notes: "", latitude: null, longitude: null,
    pod_id: null, photo_url: null, signature_url: null, createdAt: serverTimestamp()
  });
  await updateDoc(doc(db, "delivery_jobs", jobId), { total_stops: stopNumber, updatedAt: serverTimestamp() });
  await writePublicTracking(jobId);
}

export async function updateJob(jobId, patch) {
  await updateDoc(doc(db, "delivery_jobs", jobId), { ...patch, updatedAt: serverTimestamp() });
}

export function subscribeJobs(cb) {
  return onSnapshot(collection(db, "delivery_jobs"),
    (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    (e) => console.warn("jobs read:", e?.message));
}
export function subscribeJob(jobId, cb) {
  return onSnapshot(doc(db, "delivery_jobs", jobId),
    (snap) => cb(snap.exists() ? { id: snap.id, ...snap.data() } : null),
    (e) => console.warn("job read:", e?.message));
}
export function subscribeStops(jobId, cb) {
  return onSnapshot(query(collection(db, "delivery_stops"), where("job_id", "==", jobId)),
    (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() })).sort((a, b) => a.stop_number - b.stop_number)),
    (e) => console.warn("stops read:", e?.message));
}

// Stops across multiple jobs (for a driver's personal dashboard). Scoped to the
// driver's assigned jobs only — never global.
export function subscribeStopsForJobs(jobIds, cb) {
  if (!jobIds || !jobIds.length) { cb([]); return () => {}; }
  const ids = jobIds.slice(0, 10);
  return onSnapshot(query(collection(db, "delivery_stops"), where("job_id", "in", ids)),
    (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    (e) => console.warn("stops-for-jobs read:", e?.message));
}

// ---- Driver-scoped, server-constrained reads (rule-compatible isolation) ----
export function subscribeDriverJobs(email, cb) {
  const e = (email || "").toLowerCase();
  if (!e) { cb([]); return () => {}; }
  return onSnapshot(query(collection(db, "delivery_jobs"), where("driver_email", "==", e)),
    (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    (err) => console.warn("driver jobs read:", err?.message));
}
export function subscribeDriverStops(email, cb) {
  const e = (email || "").toLowerCase();
  if (!e) { cb([]); return () => {}; }
  return onSnapshot(query(collection(db, "delivery_stops"), where("driver_email", "==", e)),
    (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    (err) => console.warn("driver stops read:", err?.message));
}
export function subscribeStopDoc(stopId, cb) {
  return onSnapshot(doc(db, "delivery_stops", stopId),
    (snap) => cb(snap.exists() ? { id: snap.id, ...snap.data() } : null),
    (e) => console.warn("stop doc read:", e?.message));
}

// Assign (or clear) a driver on a job AND denormalize driver_email onto every
// stop so per-driver Firestore rules and queries work. Admin context.
export async function assignDriver(jobId, driverEmail) {
  const email = (driverEmail || "").trim().toLowerCase();
  await updateDoc(doc(db, "delivery_jobs", jobId), { driver_email: email, updatedAt: serverTimestamp() });
  const stopsSnap = await getDocs(query(collection(db, "delivery_stops"), where("job_id", "==", jobId)));
  await Promise.all(stopsSnap.docs.map((d) => updateDoc(doc(db, "delivery_stops", d.id), { driver_email: email })));
  await writePublicTracking(jobId);
  await addAudit(jobId, null, `Driver assigned: ${email || "cleared"}`, "admin");
}

// Sanitized public mirror for the client tracking link (no earnings/notes/PII beyond
// what a client already knows). Lets tracking work under strict private-collection rules.
export async function writePublicTracking(jobOrId, stops) {
  try {
    let job = jobOrId;
    if (typeof jobOrId === "string") {
      const s = await getDoc(doc(db, "delivery_jobs", jobOrId));
      if (!s.exists()) return;
      job = { id: s.id, ...s.data() };
      const ss = await getDocs(query(collection(db, "delivery_stops"), where("job_id", "==", job.id)));
      stops = ss.docs.map((d) => d.data());
    }
    const sanitized = (stops || []).map((s) => {
      const photos = (s.photos && s.photos.length)
        ? s.photos.map((p) => ({ url: p.url, caption: p.caption || "", captured_at: p.captured_at || null }))
        : (s.photo_url ? [{ url: s.photo_url, caption: "", captured_at: null }] : []);
      return {
        stop_number: s.stop_number, package_reference: s.package_reference || "",
        recipient_name: s.recipient_name, company_name: s.company_name || "",
        address_line_1: s.address_line_1 || "", city: s.city, state: s.state, zip_code: s.zip_code || "",
        status: s.status, delivered_time: s.delivered_time || null,
        recipient_received_by: s.recipient_received_by || "", photo_url: photos[0]?.url || null,
        photos
      };
    }).sort((a, b) => a.stop_number - b.stop_number);
    const delivered = sanitized.filter((s) => s.status === "delivered").length;
    const total = job.total_stops || sanitized.length;
    const payload = {
      job_reference: job.job_reference, job_name: job.job_name, client_name: job.client_name || "",
      job_date: job.job_date || "", track_token: job.track_token,
      job_status: job.job_status || (delivered >= total && total > 0 ? "Completed" : "In Progress"),
      enabled: job.customer_link_enabled !== false,
      total_stops: total, completed_stops: delivered,
      stops: sanitized, updatedAt: serverTimestamp()
    };
    // Written under BOTH the job reference (legacy /track links) and the secret
    // token (customer /delivery page) so every lookup is a single getDoc (no list query).
    await setDoc(doc(db, "public_tracking", job.job_reference), payload);
    if (job.track_token) await setDoc(doc(db, "public_tracking", job.track_token), payload);
  } catch (e) { console.warn("public tracking write:", e?.message); }
}

export async function getJobByRefAndToken(jobRef, token) {
  // Prefer the public mirror (works under strict private-collection rules).
  try {
    const m = await getDoc(doc(db, "public_tracking", jobRef));
    if (m.exists()) {
      const d = m.data();
      if (d.track_token !== token) return null;
      return { job: { id: jobRef, ...d }, stops: (d.stops || []).map((s, i) => ({ id: `${jobRef}-${s.stop_number || i}`, ...s })) };
    }
  } catch (e) { /* fall back to direct read below */ }
  const snap = await getDoc(doc(db, "delivery_jobs", jobRef));
  if (!snap.exists()) return null;
  const job = { id: snap.id, ...snap.data() };
  if (job.track_token !== token) return null;
  const stopsSnap = await getDocs(query(collection(db, "delivery_stops"), where("job_id", "==", jobRef)));
  const stops = stopsSnap.docs.map((d) => ({ id: d.id, ...d.data() })).sort((a, b) => a.stop_number - b.stop_number);
  return { job, stops };
}

// Customer Delivery Proof page — look up a job by its secret token only (single getDoc).
export async function getJobByToken(token) {
  if (!token) return null;
  const snap = await getDoc(doc(db, "public_tracking", token));
  if (!snap.exists()) return null;
  const d = snap.data();
  if (d.enabled === false) return { disabled: true };
  return {
    job: { id: d.job_reference, ...d },
    stops: (d.stops || []).map((s, i) => ({ id: `${d.job_reference}-${s.stop_number || i}`, ...s }))
  };
}

// Enable / disable (expire) the customer sharing link.
export async function setCustomerLinkEnabled(job, enabled) {
  await updateDoc(doc(db, "delivery_jobs", job.id), { customer_link_enabled: enabled, updatedAt: serverTimestamp() });
  await setDoc(doc(db, "public_tracking", job.job_reference), { enabled }, { merge: true });
  if (job.track_token) await setDoc(doc(db, "public_tracking", job.track_token), { enabled }, { merge: true });
}

// ---- Site-wide customer portal settings (Google review URL) ----
const SETTINGS_DOC = "settings";
export async function getSiteSettings() {
  try {
    const s = await getDoc(doc(db, "site_compliance", SETTINGS_DOC));
    return s.exists() ? s.data() : {};
  } catch { return {}; }
}
export async function saveSiteSettings(patch) {
  await setDoc(doc(db, "site_compliance", SETTINGS_DOC), { ...patch, updatedAt: serverTimestamp() }, { merge: true });
}

export async function addAudit(jobId, stopId, message, actor) {
  try {
    await addDoc(collection(db, "delivery_audit"), { job_id: jobId, stop_id: stopId || null, message, actor: actor || "", at: serverTimestamp() });
  } catch (e) { /* non-blocking */ }
}

export function subscribeAudit(jobId, cb) {
  return onSnapshot(query(collection(db, "delivery_audit"), where("job_id", "==", jobId)),
    (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() })).sort((a, b) => (b.at?.seconds || 0) - (a.at?.seconds || 0))),
    () => {});
}

export function compressImage(file, maxDim = 1280, quality = 0.7) {
  return new Promise((resolve) => {
    try {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        let { width, height } = img;
        if (width > height && width > maxDim) { height = Math.round(height * maxDim / width); width = maxDim; }
        else if (height > maxDim) { width = Math.round(width * maxDim / height); height = maxDim; }
        const canvas = document.createElement("canvas");
        canvas.width = width; canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        canvas.toBlob((blob) => { URL.revokeObjectURL(url); resolve(blob || file); }, "image/jpeg", quality);
      };
      img.onerror = () => { URL.revokeObjectURL(url); resolve(file); }; // HEIC etc: upload original
      img.src = url;
    } catch { resolve(file); }
  });
}

export function dataUrlToBlob(dataUrl) {
  const [head, body] = dataUrl.split(",");
  const mime = head.match(/:(.*?);/)[1];
  const bin = atob(body);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  return new Blob([arr], { type: mime });
}

async function uploadBlob(path, blob) {
  const r = ref(storage, path);
  await uploadBytes(r, blob);
  return getDownloadURL(r);
}

// Complete (or update) a delivery stop with POD. Photos are managed separately
// (see addStopPhotos) so they can be added/removed before AND after delivery.
export async function completeDelivery({ job, stop, status, recipientName, notes, signatureDataUrl, driver, coords }) {
  const jobId = job.id;
  const now = new Date();
  let signatureUrl = stop.signature_url || null;

  await addAudit(jobId, stop.id, `Driver opened Stop ${stop.stop_number}`, driver?.name || driver?.email || "driver");

  if (signatureDataUrl) {
    signatureUrl = await uploadBlob(`deliveries/${jobId}/${stop.id}/sig-${Date.now()}.png`, dataUrlToBlob(signatureDataUrl));
  }

  // Preserve the ORIGINAL delivery time — never overwrite it on later edits.
  const deliveredTime = status === "delivered" ? (stop.delivered_time || now.toISOString()) : (stop.delivered_time || null);
  const firstPhoto = stop.photos?.[0]?.url || stop.photo_url || null;

  // Proof of Delivery record
  const podRef = await addDoc(collection(db, "proof_of_delivery"), {
    stop_id: stop.id, job_id: jobId, package_reference: stop.package_reference,
    photo_url: firstPhoto, signature_url: signatureUrl,
    recipient_name: recipientName || stop.recipient_received_by || "",
    delivery_timestamp: deliveredTime || now.toISOString(),
    latitude: coords?.latitude ?? null, longitude: coords?.longitude ?? null,
    driver_id: driver?.id || null, driver_name: driver?.name || driver?.email || "",
    status, notes: notes || "", createdAt: serverTimestamp()
  });

  await updateDoc(doc(db, "delivery_stops", stop.id), {
    status,
    arrival_time: stop.arrival_time || now.toISOString(),
    delivered_time: deliveredTime,
    recipient_received_by: recipientName || "",
    driver_notes: notes || "",
    latitude: coords?.latitude ?? null, longitude: coords?.longitude ?? null,
    pod_id: podRef.id, signature_url: signatureUrl
  });

  // Recompute job progress (driver-scoped query so it works under strict rules)
  const dEmail = (driver?.email || "").toLowerCase();
  const stopsSnap = await getDocs(query(collection(db, "delivery_stops"), where("driver_email", "==", dEmail)));
  const all = stopsSnap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((s) => s.job_id === jobId);
  const delivered = all.filter((s) => s.status === "delivered").length;
  const total = job.total_stops || all.length;
  const jobStatus = delivered >= total ? "Completed" : "In Progress";
  await updateDoc(doc(db, "delivery_jobs", jobId), { completed_stops: delivered, job_status: jobStatus, updatedAt: serverTimestamp() });

  await addAudit(jobId, stop.id, `Delivery ${status} · POD generated`, driver?.name || "driver");
  await writePublicTracking({ ...job, completed_stops: delivered, total_stops: total }, all);

  if (status === "delivered") {
    notifyDelivery({ ...job, total_stops: total }, stop, status);
  }
  return podRef.id;
}

// ---- POD photo management (multiple photos, add after delivery, keep timestamp) ----

// Normalized list of a stop's POD photos (migrates legacy single photo_url).
export function stopPhotos(stop) {
  if (stop?.photos?.length) return stop.photos;
  if (stop?.photo_url) return [{ id: "legacy", url: stop.photo_url, uploaded_at: stop.delivered_time || null, uploaded_by: "" }];
  return [];
}

// Add one or more POD photos to a stop WITHOUT changing status or delivered_time.
export async function addStopPhotos({ job, stop, files, uploader, caption, capturedAt }) {
  const jobId = job.id;
  const existing = (stop.photos && stop.photos.length)
    ? [...stop.photos]
    : (stop.photo_url ? [{ id: `legacy-${Date.now()}`, url: stop.photo_url, uploaded_at: stop.delivered_time || null, uploaded_by: "", uploaded_by_email: "" }] : []);
  const nowIso = new Date().toISOString();
  for (const file of files) {
    const blob = await compressImage(file);
    const rand = Math.random().toString(36).slice(2, 8);
    const url = await uploadBlob(`deliveries/${jobId}/${stop.id}/photo-${Date.now()}-${rand}.jpg`, blob);
    existing.push({
      id: `${Date.now()}-${rand}`,
      url,
      caption: caption || "",
      captured_at: capturedAt || null,
      uploaded_at: nowIso,
      uploaded_by: uploader?.name || uploader?.email || "",
      uploaded_by_email: (uploader?.email || "").toLowerCase()
    });
  }
  await updateDoc(doc(db, "delivery_stops", stop.id), {
    photos: existing,
    photo_url: existing[0]?.url || null,
    photo_uploaded_at: nowIso
  });
  await addAudit(jobId, stop.id, `${files.length} POD photo(s) added · Stop ${stop.stop_number} (${stop.package_reference})`, uploader?.name || uploader?.email || "user");
  await writePublicTracking(jobId);
  return existing;
}

// Remove a single POD photo (keeps the delivery record intact).
export async function deleteStopPhoto({ job, stop, photoId, actor }) {
  const jobId = job.id;
  const current = stopPhotos(stop);
  const next = current.filter((p) => p.id !== photoId);
  await updateDoc(doc(db, "delivery_stops", stop.id), {
    photos: next,
    photo_url: next[0]?.url || null
  });
  await addAudit(jobId, stop.id, `POD photo removed · Stop ${stop.stop_number} (${stop.package_reference})`, actor?.name || actor?.email || "user");
  await writePublicTracking(jobId);
  return next;
}

function podFileBase(stop) {
  const name = (stop.recipient_name || "").replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "");
  return `${stop.package_reference || "stop-" + stop.stop_number}${name ? "_" + name : ""}`;
}

// Download all POD photos for a job as a ZIP, named by package ref + recipient.
export async function downloadAllPodPhotos(job, stops) {
  const JSZip = (await import("jszip")).default;
  const zip = new JSZip();
  let count = 0;
  const ordered = [...stops].sort((a, b) => a.stop_number - b.stop_number);
  for (const s of ordered) {
    const photos = stopPhotos(s);
    if (!photos.length) continue;
    const base = podFileBase(s);
    let i = 0;
    for (const p of photos) {
      i++;
      try {
        const resp = await fetch(p.url);
        const blob = await resp.blob();
        zip.file(photos.length > 1 ? `${base}_${i}.jpg` : `${base}.jpg`, blob);
        count++;
      } catch (e) { /* skip unreachable image */ }
    }
  }
  if (!count) throw new Error("No POD photos to download yet.");
  const content = await zip.generateAsync({ type: "blob" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(content);
  a.download = `${job.job_reference}_POD_Photos.zip`;
  a.click();
  URL.revokeObjectURL(a.href);
  return count;
}

// Download a single POD image with a meaningful filename.
export async function downloadPodPhoto(stop, url, index = 0, total = 1) {
  const base = podFileBase(stop);
  try {
    const resp = await fetch(url);
    const blob = await resp.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = total > 1 ? `${base}_${index + 1}.jpg` : `${base}.jpg`;
    a.click();
    URL.revokeObjectURL(a.href);
  } catch { window.open(url, "_blank"); }
}

// ---- Client sharing via Resend backend ----
function trackUrl(job) { return `${window.location.origin}/track/${job.job_reference}/${job.track_token}`; }

export async function sendPodEmail({ job, stop, emails }) {
  const backend = process.env.REACT_APP_BACKEND_URL;
  const recipients = ((emails && emails.length ? emails : job.notify_emails) || []).filter(Boolean);
  if (!backend) throw new Error("Backend not configured.");
  if (!recipients.length) throw new Error("No recipient emails set for this job.");
  const photos = stopPhotos(stop).map((p) => p.url);
  const resp = await fetch(`${backend}/api/deliveries/send-pod`, {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      recipient_emails: recipients,
      job_name: job.job_name, job_reference: job.job_reference,
      stop_number: stop.stop_number, total_stops: job.total_stops || null,
      recipient_name: stop.recipient_name || "", company_name: stop.company_name || "",
      address: `${stop.address_line_1}, ${stop.city}, ${stop.state} ${stop.zip_code}`,
      package_reference: stop.package_reference || "", status: stop.status || "delivered",
      delivered_time: stop.delivered_time ? new Date(stop.delivered_time).toLocaleString() : "",
      received_by: stop.recipient_received_by || "",
      photo_urls: photos,
      track_url: trackUrl(job)
    })
  });
  if (!resp.ok) throw new Error((await resp.json().catch(() => ({})))?.detail || "Email failed");
  return resp.json();
}

export async function sendReportEmail({ job, stops, emails }) {
  const backend = process.env.REACT_APP_BACKEND_URL;
  const recipients = ((emails && emails.length ? emails : job.notify_emails) || []).filter(Boolean);
  if (!backend) throw new Error("Backend not configured.");
  if (!recipients.length) throw new Error("No recipient emails set for this job.");
  const delivered = stops.filter((s) => s.status === "delivered").length;
  const withPhotos = stops.filter((s) => stopPhotos(s).length).length;
  const resp = await fetch(`${backend}/api/deliveries/send-report`, {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      recipient_emails: recipients,
      job_name: job.job_name, job_reference: job.job_reference,
      client_name: job.client_name || "", job_date: job.job_date || "",
      total_stops: stops.length, delivered_stops: delivered, stops_with_photos: withPhotos,
      track_url: trackUrl(job)
    })
  });
  if (!resp.ok) throw new Error((await resp.json().catch(() => ({})))?.detail || "Email failed");
  return resp.json();
}

export function mapsUrl(stop) {
  const q = encodeURIComponent(`${stop.address_line_1} ${stop.address_line_2 || ""} ${stop.city}, ${stop.state} ${stop.zip_code}`.replace(/\s+/g, " ").trim());
  return `https://www.google.com/maps/dir/?api=1&destination=${q}`;
}

// Fire-and-forget delivery email notification (server-side Resend via backend).
export async function notifyDelivery(job, stop, status) {
  const emails = (job.notify_emails || []).filter(Boolean);
  const backend = process.env.REACT_APP_BACKEND_URL;
  if (!emails.length || !backend) return;
  try {
    await fetch(`${backend}/api/deliveries/notify`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        recipient_emails: emails,
        job_name: job.job_name,
        job_reference: job.job_reference,
        stop_number: stop.stop_number,
        total_stops: job.total_stops || null,
        recipient_name: stop.recipient_name || "",
        company_name: stop.company_name || "",
        address: `${stop.address_line_1}, ${stop.city}, ${stop.state} ${stop.zip_code}`,
        package_reference: stop.package_reference || "",
        status,
        delivered_time: new Date().toLocaleString(),
        track_url: `${window.location.origin}/track/${job.job_reference}/${job.track_token}`
      })
    });
  } catch (e) { console.warn("notify failed:", e?.message); }
}
